import React from 'react';

/**
 * Set de iconos de la app. Reglas (skill better-ui):
 * - Un solo SVG por icono: los estados (hover, activo, disabled) salen de CSS
 *   porque el trazo usa `currentColor`. Nunca un asset por estado.
 * - El grosor del trazo acompaña al peso del texto de al lado: 1.5 junto a
 *   texto regular, 2 junto a semibold/bold. Por eso `strokeWidth` es prop.
 * - Grilla nativa de 24px y tamaño por className (`h-4 w-4` = 16px reales).
 */

export interface IconProps {
  className?: string;
  strokeWidth?: number;
  /** Decorativo por defecto: el nombre accesible lo pone el botón que lo contiene. */
  title?: string;
}

const base = {
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor' as const,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
};

function Svg({
  className = 'h-4 w-4',
  strokeWidth = 2,
  title,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg {...base} className={className} strokeWidth={strokeWidth}>
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}

export const RefreshIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M20 11a8 8 0 0 0-13.9-5.4L3 8.5" />
    <path d="M4 13a8 8 0 0 0 13.9 5.4L21 15.5" />
    <path d="M3 4.5v4h4" />
    <path d="M21 19.5v-4h-4" />
  </Svg>
);

export const ChevronDownIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);

export const ChevronUpIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="m18 15-6-6-6 6" />
  </Svg>
);

export const PencilIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3z" />
    <path d="m15 6 3 3" />
  </Svg>
);

export const TrashIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M4 7h16" />
    <path d="M10 11v6M14 11v6" />
    <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" />
    <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </Svg>
);

export const CloseIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M6 18 18 6M6 6l12 12" />
  </Svg>
);

export const AlertIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M10.3 4.3 2.7 17.5A2 2 0 0 0 4.4 20.5h15.2a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z" />
    <path d="M12 9.5v4" />
    <path d="M12 17h.01" />
  </Svg>
);

export const InfoIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 7.5h.01" />
  </Svg>
);

export const CheckIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Svg>
);

export const CalendarIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
    <path d="M3.5 10h17" />
    <path d="M8 3v4M16 3v4" />
  </Svg>
);

export const ExternalLinkIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M10 6H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
    <path d="M14 4h6v6" />
    <path d="M20 4 10 14" />
  </Svg>
);

export const SearchIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
);

export const DownloadIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M12 4v11" />
    <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
    <path d="M4.5 19.5h15" />
  </Svg>
);

export const CopyIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <rect x="9" y="9" width="11" height="11" rx="2.5" />
    <path d="M6 15H5.5A1.5 1.5 0 0 1 4 13.5v-8A1.5 1.5 0 0 1 5.5 4h8A1.5 1.5 0 0 1 15 5.5V6" />
  </Svg>
);

export const UserIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="8" r="4" />
    <path d="M5 21a7 7 0 0 1 14 0" />
  </Svg>
);

/**
 * Indicador de carga. Es la única animación por keyframes del set: no es un
 * cambio de estado interactivo sino una secuencia continua.
 */
export const SpinnerIcon: React.FC<IconProps> = ({ className = 'h-4 w-4', strokeWidth = 2 }) => (
  <svg
    {...base}
    className={`${className} animate-spin motion-reduce:animate-none`}
    strokeWidth={strokeWidth}
  >
    <circle cx="12" cy="12" r="9" className="opacity-25" />
    <path d="M21 12a9 9 0 0 0-9-9" />
  </svg>
);

export const PlusIcon: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);
